import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import LOGO from "../../assets/images/diligentLogoShortavif.avif";
import AdminDetailsLoader from "../../components/Skeletons/adminDetailsLoader";
import ModalWithTextBox from "../../components/ModalWithTextBox";
import NotificationModal from "../../components/NotificationModal";
import LibraryRequestActivityTimeline from "../../components/LibraryRequests/LibraryRequestActivityTimeline";
import LibraryRequestHeaderActions from "../../components/LibraryRequests/LibraryRequestHeaderActions";
import LibraryRequestOverview from "../../components/LibraryRequests/LibraryRequestOverview";
import LibraryRequestPreviewModal from "../../components/LibraryRequests/LibraryRequestPreviewModal";
import { useLibraryRequestDetails } from "../../custom-hooks/queries";
import { useLibraryRequestActions } from "../../custom-hooks/useLibraryRequestActions";
import { useUserRole } from "../../utils/helperMethods";
import { getLibraryRequestsPathForRole } from "../../utils/libraryRequestsEmailLinkPaths";
import {
  buildLibraryRequestOverviewRows,
  formatLibraryRequestDate,
  resolveLibraryRequestAudience,
  resolveLibraryRequestContacts,
} from "../../utils/libraryRequestDetailsView";

export default function LibraryRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = useUserRole();
  const audience = resolveLibraryRequestAudience(role);
  const { data: details, isLoading, isError, refetch } = useLibraryRequestDetails(id);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const actions = useLibraryRequestActions({
    requestId: id,
    documentId: details?.document_id,
    onChanged: refetch,
  });
  const reportName = details?.report_type || details?.document_name;

  return (
    <div className="container">
      <div className="p-7 border-y border-gray-300 bg-slate-50 mb-5">
        <div className="flex items-center text-sm">
          <button
            type="button"
            className="text-gray-500 font-semibold hover:text-gray-700"
            onClick={() => navigate(getLibraryRequestsPathForRole(role))}
          >
            Library Requests
          </button>
          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
          <span className="text-gray-900 font-semibold truncate max-w-xs" title={reportName}>
            {reportName || "Request Details"}
          </span>
        </div>
        <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center">
            <span className="bg-white border border-gray-300 rounded-md px-7 py-5 mr-5">
              <img src={LOGO} alt="Logo" className="h-10 w-10" />
            </span>
            <span>
              <p className="text-md font-semibold">{details?.provider_name}</p>
              <p className="text-sm text-gray-500">{reportName}</p>
              {details?.requested_on && (
                <p className="text-sm text-gray-500">{formatLibraryRequestDate(details.requested_on)}</p>
              )}
            </span>
          </div>
          <LibraryRequestHeaderActions
            details={details}
            onPreview={() => setPreviewOpen(true)}
            onDownload={actions.download}
            onAccept={actions.accept}
            onReject={() => setRejectOpen(true)}
            isResponding={actions.isResponding}
            isDownloading={actions.isDownloading}
          />
        </div>
      </div>
      <div className="border-b border-gray-200 px-5">
        <nav className="-mb-px flex items-center">
          <span className="whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm border-indigo-600 text-indigo-600">
            Overview
          </span>
        </nav>
      </div>
      {isLoading && (
        <div className="w-full md:w-[70%] border-r border-gray-300 px-3 md:mr-5 lg:mr-8">
          <AdminDetailsLoader />
        </div>
      )}
      {isError && (
        <p className="px-8 py-10 text-sm text-gray-600">
          This library request is not available or you do not have access to it.
        </p>
      )}
      {details && (
        <div className="mb-28 px-5 flex justify-center md:justify-between items-stretch flex-col md:flex-row">
          <div className="w-full md:w-[70%] border-r border-gray-300 px-3 mr-8">
            <LibraryRequestOverview
              rows={buildLibraryRequestOverviewRows(details, audience, {
                onPreview: () => setPreviewOpen(true),
              })}
            />
          </div>
          <div className="w-full md:w-[30%] mt-10 md:mt-0 mb-20">
            <LibraryRequestActivityTimeline
              activity={details.activity}
              contacts={resolveLibraryRequestContacts(details)}
            />
          </div>
        </div>
      )}
      <LibraryRequestPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        requestId={id}
        allowFullReportPreview={details?.allow_full_report_preview}
      />
      <ModalWithTextBox
        open={rejectOpen}
        setOpen={setRejectOpen}
        onClick={actions.submitReject}
        id={id}
        refetch={refetch}
      />
      <NotificationModal
        open={actions.notification.open}
        setOpen={actions.setNotificationOpen}
        title={actions.notification.title}
        desc={actions.notification.message}
        error={actions.notification.error}
      />
    </div>
  );
}
